import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ProductDetailsPanel = ({ selectedProduct, onClose, onEdit }) => {
  if (!selectedProduct) {
    return (
      <div className="bg-surface rounded-lg border border-border p-6 text-center">
        <Icon name="Package" size={48} className="text-text-muted mx-auto mb-4" />
        <h3 className="text-lg font-body-medium text-text-primary mb-2">
          Selecciona un producto
        </h3>
        <p className="text-sm text-text-muted">
          Haz clic en un producto de la tabla para ver sus detalles aquí
        </p>
      </div>
    );
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  const getStockStatusColor = (stock, minStock = 10) => {
    if (stock === 0) return 'text-error';
    if (stock <= minStock) return 'text-warning';
    return 'text-success';
  };

  const specifications = [
    { label: 'Marca', value: selectedProduct.brand },
    { label: 'Modelo', value: selectedProduct.model },
    { label: 'Peso', value: selectedProduct.weight ? `${selectedProduct.weight} kg` : 'N/A' },
    { label: 'Dimensiones', value: selectedProduct.dimensions || 'N/A' },
    { label: 'Color', value: selectedProduct.color || 'N/A' },
    { label: 'Material', value: selectedProduct.material || 'N/A' }
  ];

  return (
    <div className="bg-surface rounded-lg border border-border overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <h3 className="text-lg font-body-medium text-text-primary">
          Detalles del Producto
        </h3>
        <button
          onClick={onClose}
          className="p-1 hover:bg-secondary-100 rounded transition-micro"
        >
          <Icon name="X" size={20} className="text-text-muted" />
        </button>
      </div>

      {/* Product Image */}
      <div className="p-4 border-b border-border">
        <div className="w-full h-48 rounded-lg overflow-hidden bg-secondary-100 mb-4">
          <Image
            src={selectedProduct.image}
            alt={selectedProduct.name}
            className="w-full h-full object-cover"
          />
        </div>
        
        {/* Product Gallery */}
        {selectedProduct.gallery && selectedProduct.gallery.length > 0 && (
          <div className="flex space-x-2 overflow-x-auto">
            {selectedProduct.gallery.map((image, index) => (
              <div key={index} className="w-16 h-16 rounded-md overflow-hidden bg-secondary-100 flex-shrink-0">
                <Image
                  src={image}
                  alt={`${selectedProduct.name} ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Product Info */}
      <div className="p-4 space-y-4">
        {/* Basic Info */}
        <div>
          <h4 className="text-base font-body-medium text-text-primary mb-2">
            {selectedProduct.name}
          </h4>
          <div className="flex items-center space-x-4 text-sm">
            <span className="text-text-muted">SKU:</span>
            <span className="font-data text-text-primary">{selectedProduct.sku}</span>
          </div>
          <div className="flex items-center space-x-4 text-sm mt-1">
            <span className="text-text-muted">Categoría:</span>
            <span className="text-text-primary">{selectedProduct.category}</span>
          </div>
        </div>

        {/* Price & Stock */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <span className="text-sm text-text-muted block">Precio</span>
            <span className="text-lg font-body-medium text-text-primary">
              {formatPrice(selectedProduct.price)}
            </span>
          </div>
          <div>
            <span className="text-sm text-text-muted block">Stock</span>
            <span className={`text-lg font-body-medium ${getStockStatusColor(selectedProduct.stock, selectedProduct.minStock)}`}>
              {selectedProduct.stock} unidades
            </span>
          </div>
        </div>

        {/* Description */}
        {selectedProduct.description && (
          <div>
            <h5 className="text-sm font-body-medium text-text-primary mb-2">
              Descripción
            </h5>
            <p className="text-sm text-text-muted leading-relaxed">
              {selectedProduct.description}
            </p>
          </div>
        )}

        {/* Specifications */}
        <div>
          <h5 className="text-sm font-body-medium text-text-primary mb-3">
            Especificaciones
          </h5>
          <div className="space-y-2">
            {specifications.map((spec, index) => (
              <div key={index} className="flex justify-between items-center py-1">
                <span className="text-sm text-text-muted">{spec.label}:</span>
                <span className="text-sm text-text-primary">{spec.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Supplier Info */}
        {selectedProduct.supplier && (
          <div>
            <h5 className="text-sm font-body-medium text-text-primary mb-2">
              Proveedor
            </h5>
            <div className="text-sm text-text-muted">
              <div>{selectedProduct.supplier.name}</div>
              <div>{selectedProduct.supplier.contact}</div>
            </div>
          </div>
        )}

        {/* Last Updated */}
        <div className="pt-4 border-t border-border">
          <div className="flex items-center justify-between text-sm">
            <span className="text-text-muted">Última actualización:</span>
            <span className="text-text-primary">
              {new Date(selectedProduct.lastUpdated).toLocaleDateString('es-ES', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="pt-4 border-t border-border">
          <Button
            variant="primary"
            fullWidth
            onClick={() => onEdit(selectedProduct.id)}
            iconName="Edit2"
            iconSize={16}
          >
            Editar Producto
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailsPanel;